import * as React from "react";
import {inject, observer} from "mobx-react";
import {ToggleContainer} from "../../../../../../widgets/container/ToggleContainer";
import type {Report} from "../../../../../../../data/models/Report";
import type {ReportSharingSettingsData} from "../../../../../../../generated/api/reports";
import type {AppState} from "../../../../../../../data/state/AppState";
import {AddUserOrUserGroup} from "../../../../../../widgets/user/AddUserOrUserGroup";
import {ShareOption} from "../../../../view/sharing/ShareOption";
import {Permission, XyiconFeature} from "../../../../../../../generated/api/base";
import type {User} from "../../../../../../../data/models/User";
import type {UserGroup} from "../../../../../../../data/models/UserGroup";
import {IconButton} from "../../../../../../widgets/button/IconButton";
import {Initials} from "../../../../../../widgets/Initials";
import {ReactUtils} from "../../../../../../utils/ReactUtils";
import {Functions} from "../../../../../../../utils/function/Functions";
import {StringUtils} from "../../../../../../../utils/data/string/StringUtils";
import {SVGIcon} from "../../../../../../widgets/button/SVGIcon";
import type {Collection} from "../../../../../../../data/models/abstract/Collection";

interface IReportSharingSectionProps {
	readonly report: Report;
	readonly appState?: AppState;
}

interface IReportSharingSectionState {
	search: string;
	newPermission: Permission;
}

/**
 * Note: this component is very similar to EditViewSharing
 */
@inject("appState")
@observer
export class ReportSharingSection extends React.Component<IReportSharingSectionProps, IReportSharingSectionState> {
	constructor(props: IReportSharingSectionProps) {
		super(props);
		this.state = {
			search: "",
			newPermission: Permission.View,
		};
	}

	private onSearch = (value: string) => {
		this.setState({
			search: value,
		});
	};

	private onAddClick = (id: string, feature: XyiconFeature.User | XyiconFeature.UserGroup) => {
		const {report, appState} = this.props;
		const item = appState.actions.getFeatureItemById<User | UserGroup>(id, feature);
		const sharingSettings = report.sharingSettings || [];
		const canEditSharedReport = this.getNewSharingPermission(item, feature);

		if (feature === XyiconFeature.User) {
			sharingSettings.push({
				userID: item.id,
				userGroupID: null,
				canEditSharedReport,
			});

			appState.actions.updateReport(report);
		} else if (feature === XyiconFeature.UserGroup) {
			sharingSettings.push({
				userID: null,
				userGroupID: item.id,
				canEditSharedReport,
			});

			appState.actions.updateReport(report);
		}

		this.setState({
			search: "",
		});
	};

	private onRemoveUserGroup = (userGroup: UserGroup) => {
		const {report, appState} = this.props;

		if (this.isGlobalAdministratorUserGroup(userGroup)) {
			return;
		}

		report.removeUserGroupFromSharing(userGroup.id);
		appState.actions.updateReport(report);
	};

	private onRemoveUser = (user: User) => {
		const {report, appState} = this.props;

		report.removeUserFromSharing(user.id);
		appState.actions.updateReport(report);
	};

	private onChangeSharingPermission = (sharing: ReportSharingSettingsData, permission: Permission) => {
		const {report, appState} = this.props;

		if (report.isGlobal) {
			const canEditSharedReport = this.isGlobalAdministratorSharing(sharing);

			if (sharing.canEditSharedReport !== canEditSharedReport) {
				sharing.canEditSharedReport = canEditSharedReport;
				appState.actions.updateReport(report);
			}
			return;
		}

		sharing.canEditSharedReport = permission === Permission.Update;
		appState.actions.updateReport(report);
	};

	private getNewSharingPermission(item: User | UserGroup, feature: XyiconFeature.User | XyiconFeature.UserGroup) {
		if (this.props.report.isGlobal) {
			return feature === XyiconFeature.UserGroup && this.isGlobalAdministratorUserGroup(item as UserGroup);
		}

		return this.state.newPermission === Permission.Update;
	}

	private getUserGroupForSharing = (sharing: ReportSharingSettingsData): UserGroup | undefined => {
		const userGroups = this.props.appState.lists[XyiconFeature.UserGroup] as Collection<UserGroup>;

		return userGroups?.getById(sharing.userGroupID);
	};

	private isGlobalAdministratorUserGroup = (userGroup?: UserGroup | null) => {
		const administratorUserGroupId = this.props.appState.organization?.settings?.userGroups?.administrators;

		return this.props.report.isGlobal && !!userGroup && (userGroup.id === administratorUserGroupId || userGroup.name === "Administrators");
	};

	private isGlobalAdministratorSharing = (sharing: ReportSharingSettingsData) => {
		return this.isGlobalAdministratorUserGroup(this.getUserGroupForSharing(sharing));
	};

	private renderLockedPermission = (permission: Permission.Update | Permission.View) => {
		return (
			<ShareOption
				value={permission}
				onChange={Functions.emptyFunction}
				hasOwner={false}
				disabled={true}
			/>
		);
	};

	public override render() {
		const {report, appState} = this.props;
		const {search} = this.state;
		const users = appState.lists[XyiconFeature.User] as Collection<User>;
		const userGroups = appState.lists[XyiconFeature.UserGroup] as Collection<UserGroup>;
		const reportSharing = report.sharingSettings || [];
		const userGroupSharingList = reportSharing.filter((sh) => sh.userGroupID);
		const userSharingList = reportSharing.filter((sh) => sh.userID);
		const exceptionIds: string[] = [
			...userGroupSharingList.map((sh) => sh.userGroupID),
			...userSharingList.map((sh) => sh.userID),
			appState.user?.id,
		];
		const reportOwner = appState.actions.getFeatureItemById<User>(report.ownedBy, XyiconFeature.User);

		return (
			<ToggleContainer
				title="Sharing"
				open={true}
				className="ReportSharingSection"
			>
				<AddUserOrUserGroup
					search={search}
					onSearch={this.onSearch}
					onAdd={this.onAddClick}
					exceptions={exceptionIds}
					report={this.props.report}
				/>
				{!this.props.report.isGlobal && (
					<div className="section">
						<h3>Owner</h3>
						<div className="list">
							<div className="item hbox owner">
								<div className="avatar">
									{reportOwner?.profileFileName ? (
										<img
											src={reportOwner.profileFileName}
											alt={`${reportOwner?.fullName} profile image`}
										/>
									) : (
										<Initials name={reportOwner?.fullName || reportOwner?.email} />
									)}
								</div>
								<div className="vbox flex_1">
									<div className="name">{`${reportOwner?.fullName}${reportOwner === appState.user ? " (Me)" : ""}`}</div>
									<div className="email">{reportOwner?.email}</div>
								</div>
								<SVGIcon
									icon="crown"
									classNames="owner-icon"
								/>
							</div>
						</div>
					</div>
				)}
				<div className="section">
					<h3>User Groups</h3>
					{userGroupSharingList.find((sharing) => userGroups.getById(sharing.userGroupID)) ? (
						userGroupSharingList
							.toSorted((a: ReportSharingSettingsData, b: ReportSharingSettingsData) =>
								StringUtils.sortIgnoreCase(userGroups.getById(a.userGroupID).name, userGroups.getById(b.userGroupID).name),
							)
							.map((sharing, index) => {
								const userGroup = userGroups.getById(sharing.userGroupID);

								if (!userGroup) {
									return null;
								}
								const isGlobalAdministratorUserGroup = this.isGlobalAdministratorUserGroup(userGroup);
								const permission =
									isGlobalAdministratorUserGroup || (!report.isGlobal && sharing.canEditSharedReport) ? Permission.Update : Permission.View;

								return (
									<div
										key={index}
										className="item hbox"
									>
										<div className="avatar">
											<Initials name={userGroup.name} />
										</div>
										<div className="vbox flex_1">
											<div className="name">{userGroup.name}</div>
											<div className="counter">{userGroup.renderMemberCount()}</div>
										</div>
										{report.isGlobal ? (
											this.renderLockedPermission(permission)
										) : (
											<ShareOption
												value={permission}
												onChange={(permission) => {
													this.onChangeSharingPermission(sharing, permission);
												}}
												hasOwner={false}
											/>
										)}
										{!isGlobalAdministratorUserGroup && (
											<IconButton
												icon="delete"
												onClick={() => {
													this.onRemoveUserGroup(userGroup);
												}}
											/>
										)}
									</div>
								);
							})
					) : (
						<div className="empty">
							<div>Report not shared with a user group</div>
						</div>
					)}
				</div>
				<div className="section">
					<h3>Users</h3>
					{userSharingList.find((sharing) => users.getById(sharing.userID)) ? (
						userSharingList
							.toSorted((a: ReportSharingSettingsData, b: ReportSharingSettingsData) =>
								StringUtils.sortIgnoreCase(users.getById(a.userID)?.fullName, users.getById(b.userID)?.fullName),
							)
							.map((sharing, index) => {
								const user = users.getById(sharing.userID);

								if (!user) {
									return null;
								}
								const permission = report.isGlobal ? Permission.View : sharing.canEditSharedReport ? Permission.Update : Permission.View;

								return (
									<div
										key={index}
										className={ReactUtils.cls("item hbox", {unregistered: (users.getById(sharing.userID) as User).status === "invited"})}
									>
										<div className="avatar">
											{user.profileFileName ? (
												<img
													src={user.profileFileName}
													alt={`${user.fullName} profile image`}
												/>
											) : (
												<Initials name={user.fullName || user.email} />
											)}
										</div>
										<div className="vbox flex_1">
											<div className="name">{user.fullName}</div>
											<div className="email">{user.email}</div>
										</div>
										{report.isGlobal ? (
											this.renderLockedPermission(permission)
										) : (
											<ShareOption
												value={permission}
												onChange={(permission) => {
													this.onChangeSharingPermission(sharing, permission);
												}}
												hasOwner={false}
												hasEdit={appState.user?.isAdmin && user.isAdmin && (sharing.canEditSharedReport || report.ownedBy === appState.user?.id)}
											/>
										)}
										<IconButton
											icon="delete"
											onClick={() => {
												this.onRemoveUser(user);
											}}
										/>
									</div>
								);
							})
					) : (
						<div className="empty">
							<div>Report not shared with a user</div>
						</div>
					)}
				</div>
			</ToggleContainer>
		);
	}
}
